// This component is used a generic card to display data for either excursions or OBS
import { Text } from '../text/Text';
import { Button } from '../button/Button';
import { Image } from '../image/Image';
import styles from './postbookingcard.module.scss';
import { memo } from 'react';
import {
  PostBookingCardDescription,
  PostBookingCardPreTitle,
  PostBookingCardPriceSection,
  PostBookingCardProps,
  PostBookingCardTitle,
} from '@msc-b2c/interfaces/interfaces';
import {
  getCurrencySymbol,
  getPriceCurrencyOrder,
  replacePlaceholders,
  RootState,
  useAssetAltText,
} from '@msc-b2c/shared-utils';
import { useSelector } from 'react-redux';
import { buildTextFromItinerary } from './util/postBookingCardUtil';
import { Chip } from '../chip/Chip';
import clsx from 'clsx';

export const PostBookingCard = ({
  id,
  image,
  title,
  children,
  displayedPills,
  placeholderImagePath,
  imageCode,
  imageType,
  onClick,
  clickDisabled = false,
}: PostBookingCardProps) => {
  const localizedAlt = useAssetAltText(imageCode, imageType ?? 'obs');
  const altText = (imageCode && imageType ? localizedAlt : '') || title || '';
  return (
    <div
      className={clsx(styles.postBookingCard, {
        [styles.noReservation]: clickDisabled,
      })}
      tabIndex={0}
      data-code={id}
      onClick={() => !clickDisabled && onClick?.()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick?.(e);
        }
      }}
    >
      {' '}
      {displayedPills && displayedPills.length > 0 && (
        <div className={styles.pillsBar}>
          {displayedPills.map((pill, index) => (
            <Chip
              key={`${index}-${pill.pillLabel}`}
              icon={pill.pillIcon ?? ''}
              text={pill.pillLabel}
              ribbonBGColor={pill.backgroundColor}
              ribbonTextColor={pill.fontColor}
              isBadge
              styleOption={styles.pill}
              isIconOnTheLeft={!!pill.iconOnTheLeft}
            />
          ))}
        </div>
      )}
      <div className={styles.imageArea}>
        <Image
          src={image}
          alt={altText}
          styleOption={styles.logo}
          placeholderImagePath={placeholderImagePath}
        />
      </div>
      <div className={styles.textArea}>
        <div className={styles.cardBody}>{children}</div>
      </div>
    </div>
  );
};

PostBookingCard.PreTitle = memo(function PreTitle({
  text,
  day,
  portCode,
  startDate,
  dayAtSeaLabelValue,
  explorerPackageLabel,
}: PostBookingCardPreTitle) {
  const itineraryData = useSelector((state: RootState) => state.myCruise.itinerary);
  text =
    explorerPackageLabel && explorerPackageLabel !== ''
      ? explorerPackageLabel
      : buildTextFromItinerary(itineraryData, day, portCode, startDate, dayAtSeaLabelValue, text);
  return (
    <div className={styles.preTitleArea}>
      <Text
        text={text}
        styleOption={styles.preTitle}
      />
    </div>
  );
});

PostBookingCard.Title = memo(function title({ packageTitle }: PostBookingCardTitle) {
  return (
    <div className={styles.titleArea}>
      <Text
        text={packageTitle}
        styleOption={styles.packageTitle}
      />
    </div>
  );
});

PostBookingCard.Description = memo(function description({
  descriptionItems,
}: PostBookingCardDescription) {
  return (
    <div className={styles.descriptionArea}>
      {descriptionItems.map(
        ({ descriptionLabel, descriptionValue }, index) =>
          descriptionLabel &&
          descriptionValue && (
            <p
              className={styles.descriptionItem}
              key={`${index}-${descriptionLabel}`}
            >
              <span className={styles.itemLabel}>{descriptionLabel}</span>
              <span className={styles.itemValue}>{descriptionValue}</span>
            </p>
          )
      )}
    </div>
  );
});

PostBookingCard.PriceSection = memo(function priceSection({
  prices,
  buttonLabel,
  handleButtonClick,
  displayButton = true,
}: PostBookingCardPriceSection) {
  if (!prices || prices.length === 0) return null;

  const currencySymbol = getCurrencySymbol();
  const priceCurrencyOrder = getPriceCurrencyOrder();
  return (
    <div className={styles.cardFooter}>
      <div className={styles.priceContainer}>
        {prices.map(({ isPromo, beforeDiscount, afterDiscount, label }, idx) => {
          const showTwoPrices =
            isPromo &&
            beforeDiscount &&
            afterDiscount &&
            Number.parseInt(beforeDiscount) > Number.parseInt(afterDiscount);

          return (
            <div
              key={label ?? idx}
              className={styles.priceArea}
            >
              {showTwoPrices && (
                <Text
                  text={replacePlaceholders(priceCurrencyOrder, {
                    currency: currencySymbol,
                    price: beforeDiscount,
                  })}
                  styleOption={clsx(styles.price, styles['price-strikethrough'])}
                />
              )}
              <Text
                text={replacePlaceholders(priceCurrencyOrder, {
                  currency: currencySymbol,
                  price: afterDiscount,
                })}
                styleOption={clsx(styles.price, {
                  [styles['price-discount']]: showTwoPrices,
                })}
              />
              <Text
                text={label}
                styleOption={styles.priceLabel}
              />
            </div>
          );
        })}
      </div>
      {buttonLabel && displayButton && (
        <div className={styles.buttonArea}>
          <Button
            text={buttonLabel}
            styleOption={`cmp-button__primary  ${styles.button}`}
            onClick={handleButtonClick}
          />
        </div>
      )}
    </div>
  );
});
